import React from "react";
import {
    CounterType,
    OperationPageType,
    PalletPageType,
    PreparationPageType,
    PreparationSectionType,
    routeBatchPreparation,
    routeBlueBrandActiveReturns,
    routeBlueBrandFinalizedReturns,
    routeBlueBrandReturns,
    routeBulkParcelUpload,
    routeCanceledParcel,
    routeConsultShippedParcel,
    routeDeliveryOrderCanceled,
    routeExportOperationProducts,
    routeImportData,
    routeOperationPreparation,
    routePalletManagement,
    routePalletPreparation,
    routePalletShipping,
    routeParcelManagement,
    routePickAndPack,
    routePrepareParcelShipping,
    routeReporting,
    routeReviewReadyToShip,
    routeShipParcel,
    routeShippedPallets,
    routeStockouts,
} from "../../navigation/preparation-route-props";
import { PanelMenuType } from "../../../constants/constants";
import { context } from "../../../context/context";
import { Permission } from "../../../context/permission";
import { operationContext } from "../../../context/operation-context";
import { IPanelMenuItem, VpMenu } from "../../../common/component/menu/vp-menu";
import { menuOperationModel } from "./preparation-menu-model";
import { OperationCounterType, PalletCounterType } from "../../constants/constants";
import { updateOperationUrl } from "../../navigation/operation-route-parser";
import { updatePalletUrl } from "../../navigation/pallet-route-parser";
import { VpMenuContextProvider } from "../../../common/component/menu/vp-menu-context";
import { VpMenuCategory } from "../../../common/component/menu/vp-menu-category";
import { VpMenuItem } from "../../../common/component/menu/vp-menu-item";
import { Observer } from "mobx-react";
import { AllInbox, ImportExport, TaskAlt } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { FeatureToggle, IsFeatureToggleEnabled } from "../../../helpers/is-feature-toggle-enabled";

const counterRouteMap = new Map<PreparationPageType, CounterType>([
    [routeBatchPreparation, "NbDoAvailable"],
    [routeParcelManagement, "NbParcelParcelled"],
    [routePrepareParcelShipping, "NbParcelLabeled"],
    [routeShipParcel, "NbParcelReadyToShip"],
    [routeReviewReadyToShip, "NbParcelReadyToShip"],
    [routeConsultShippedParcel, "NbParcelShipped"],
    [routeCanceledParcel, "NbParcelCanceled"],
    [routeDeliveryOrderCanceled, "NbDoCanceled"],
    [routeStockouts, "NbDoWithStockouts"],
    [routePalletManagement, "NbPalletOpen"],
    [routePalletShipping, "NbPalletClosed"],
    [routeShippedPallets, "NbPalletShipped"],
]);

export interface IVPPreparationMenu {
    defaultRouteExpanded: PreparationSectionType;
}

export const VPPreparationMenu = (props: IVPPreparationMenu) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const menuItemList: Array<IPanelMenuItem<PreparationSectionType, PreparationPageType>> = [{
            defaultExpanded: props.defaultRouteExpanded === routeOperationPreparation,
            icon: "",
            label: "navigation.panel.parcelPreparation",
            key: "OPERATION_PREPARATION" as PanelMenuType,
            route: routeOperationPreparation,
            items: [{
                icon: "archive",
                iconLabel: "navigation.createParcel",
                route: routeBatchPreparation,
            }, {
                icon: "print",
                iconLabel: "navigation.parcelManagement",
                route: routeParcelManagement,
            }, {
                icon: "assignment_turned_in",
                iconLabel: "navigation.prepareParcelShipping",
                route: routePrepareParcelShipping,
                eligibilityValidator: () => context.hasPermission(Permission.FeatureReadyToShip),
            }, {
                icon: "local_shipping",
                iconLabel: "navigation.shipParcel",
                route: routeShipParcel,
                eligibilityValidator: () => !context.hasPermission(Permission.FeaturePalletWorkflow) || !operationContext.isPalletOperationOrEmpty,
            }, {
                icon: AllInbox,
                iconLabel: "navigation.reviewReadyToShip",
                route: routeReviewReadyToShip,
                eligibilityValidator: () => context.hasPermission(Permission.FeatureParcelStockouts) &&
                    context.hasPermission(Permission.FeaturePalletWorkflow) && operationContext.isPalletOperationOrEmpty,
            }, {
                icon: "content_paste",
                iconLabel: "navigation.consultShippedParcel",
                route: routeConsultShippedParcel,
                eligibilityValidator: () => !context.hasPermission(Permission.FeaturePalletWorkflow) || !operationContext.isPalletOperationOrEmpty,
            }, {
                icon: "delete_forever",
                iconLabel: "navigation.canceledParcels",
                route: routeCanceledParcel,
                eligibilityValidator: () => context.isAdministrator || context.isSuperAdministrator,
            }, {
                icon: "cancel",
                iconLabel: "navigation.cancelDo",
                route: routeDeliveryOrderCanceled,
            }, {
                icon: "report_problem",
                iconLabel: "navigation.stockouts",
                route: routeStockouts,
                eligibilityValidator: () => IsFeatureToggleEnabled(FeatureToggle.STOCKOUTS),
            }, {
                icon: "insert_chart",
                iconLabel: "navigation.reporting",
                route: routeReporting,
            }],
        }, {
            defaultExpanded: props.defaultRouteExpanded === routePalletPreparation,
            icon: "",
            label: "navigation.panel.palletPreparation",
            key: "PALLET_PREPARATION" as PanelMenuType,
            route: routePalletPreparation,
            eligibilityValidator: () => context.hasPermission(Permission.FeaturePalletWorkflow),
            items: [{
                icon: "archive",
                iconLabel: "navigation.managePallets",
                route: routePalletManagement,
            }, {
                icon: "local_shipping",
                iconLabel: "navigation.shipPallets",
                route: routePalletShipping,
            }, {
                icon: "content_paste",
                iconLabel: "navigation.shippedPallets",
                route: routeShippedPallets,
            }],
        }, {
        icon: "",
        label: "navigation.panel.others",
        key: "OTHERS" as PanelMenuType,
        route: routeOperationPreparation,
        items: [{
            icon: "grid_on",
            iconLabel: "navigation.pickAndPack",
            route: routePickAndPack,
            eligibilityValidator: () => !context.hasPermission(Permission.FeatureBlueBrand),
        }, {
            icon: "note_add",
            iconLabel: "navigation.importDataFile",
            route: routeImportData,
        }, {
            icon: "library_add",
            iconLabel: "navigation.bulkParcelUpload",
            route: routeBulkParcelUpload,
        }, {
            icon: "shop_two",
            iconLabel: "navigation.exportOperationProducts",
            route: routeExportOperationProducts,
        }],
    }, {
        icon: "",
        label: "navigation.panel.blueBrandReturns",
        defaultExpanded: props.defaultRouteExpanded === routeBlueBrandReturns,
        key: "BTM_RETURNS" as PanelMenuType,
        route: routeBlueBrandReturns,
        eligibilityValidator: () => context.hasPermission(Permission.FeatureEnableBlueBrandReturns),
        items: [{
            icon: ImportExport,
            iconStyle: { transform: "rotate(90deg)" },
            iconLabel: "navigation.blueBrandActiveReturns",
            route: routeBlueBrandActiveReturns,
        }, {
            icon: TaskAlt,
            iconLabel: "navigation.blueBrandFinalizedReturns",
            route: routeBlueBrandFinalizedReturns,
        }],
    }];

    const isRouteActive = (sectionRoute: PreparationSectionType, route: PreparationPageType) => {
        return pathname.startsWith(`/${sectionRoute}/${route}`);
    };

    const getBadgeValue = (sectionRoute: PreparationSectionType, route: PreparationPageType) => {
        const counter = !context.hasPermission(Permission.FeatureReadyToShip) && route === routeShipParcel ?
            "NbParcelLabeled" :
            counterRouteMap.get(route);

        if (counter === undefined) {
            return undefined;
        }

        return sectionRoute === "pallet"
            ? menuOperationModel.getPalletCounter(counter as PalletCounterType)
            : menuOperationModel.getCounter(counter as OperationCounterType);
    };

    const changeTab = (sectionRoute: PreparationSectionType, route: PreparationPageType) => {
        if (sectionRoute === "operations") {
            navigate(changeOperationTab(route as OperationPageType));
        } else if (sectionRoute === "pallet") {
            navigate(changePalletTab(route as PalletPageType));
        } else {
            navigate(`/${sectionRoute}/${route}`);
        }
    };

    const changeOperationTab = (route: OperationPageType) => {
        return updateOperationUrl(pathname, {
            page: route,
        });
    };

    const changePalletTab = (route: PalletPageType) => {
        return updatePalletUrl(pathname, {
            page: route,
        });
    };

    return <Observer>
        {() => <VpMenuContextProvider>
            <VpMenu key={`menu-${menuItemList.find(i => i.defaultExpanded)?.key ?? "default"}`}
                    menuItemList={menuItemList}>
                {menuItemList.filter(itemPanel => itemPanel.eligibilityValidator ? itemPanel.eligibilityValidator() : true)
                    .map((itemPanel, idx) =>
                        <VpMenuCategory key={idx} itemPanel={itemPanel}>
                            {itemPanel.items.filter(item => item.eligibilityValidator ? item.eligibilityValidator() : true)
                                .map(item =>
                                    <VpMenuItem key={`${itemPanel.route}-${item.route}`} item={item} sectionRoute={itemPanel.route}
                                                isRouteActive={isRouteActive(itemPanel.route, item.route)}
                                                badgeValue={getBadgeValue(itemPanel.route, item.route)}
                                                changeTab={changeTab} />)}
                    </VpMenuCategory>)}
            </VpMenu>
        </VpMenuContextProvider>}
    </Observer>;
};
